import { useState, useEffect, useRef } from 'react';

export default function Logs() {
  const [lines, setLines] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const [connected, setConnected] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);

  useEffect(() => {
    const es = new EventSource('/api/log/stream');
    es.onopen = () => setConnected(true);
    es.onmessage = (event) => {
      setLines((prev) => {
        const next = [...prev, event.data];
        return next.length > 500 ? next.slice(-500) : next;
      });
    };
    es.onerror = () => {
      setConnected(false);
      es.close();
    };
    return () => es.close();
  }, []);

  useEffect(() => {
    if (autoScrollRef.current && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [lines]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 50;
  };

  const filtered = filter
    ? lines.filter((l) => l.toLowerCase().includes(filter.toLowerCase()))
    : lines;

  const getLineClass = (line: string) => {
    if (line.includes('ERROR')) return 'text-trader-loss';
    if (line.includes('WARNING')) return 'text-trader-warn';
    if (line.includes('INFO')) return 'text-trader-muted';
    return 'text-trader-border';
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Logs</h2>
        <div className="flex items-center gap-3">
          <span className={`text-xs ${connected ? 'text-trader-profit' : 'text-trader-loss'}`}>
            {connected ? 'Live' : 'Disconnected'}
          </span>
          <input
            type="text"
            placeholder="Filter logs..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-trader-card border border-trader-border rounded px-3 py-1 text-sm text-trader-text placeholder:text-trader-muted w-48"
          />
          <button
            onClick={() => setLines([])}
            className="px-3 py-1 text-xs rounded border border-trader-border text-trader-muted hover:text-trader-text"
          >
            Clear
          </button>
        </div>
      </div>

      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 bg-black/50 border border-trader-border rounded-lg p-4 overflow-y-auto font-mono text-xs leading-relaxed min-h-[calc(100vh-12rem)]"
      >
        {filtered.length === 0 ? (
          <p className="text-trader-muted">
            {filter ? 'No matching log lines' : 'Waiting for log events...'}
          </p>
        ) : (
          filtered.map((line, i) => (
            <div key={i} className={`whitespace-pre-wrap break-all ${getLineClass(line)}`}>
              {line}
            </div>
          ))
        )}
      </div>

      <div className="text-xs text-trader-muted text-right">
        {filtered.length} / {lines.length} lines
      </div>
    </div>
  );
}
